import { memo, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import { styled } from '@mui/system';
import Bar from '@components/Charts/Bar';
import { Typography } from '@components/Typography';
import { I18N_DATE_FORMAT } from '@helpers/dates';
import { checkIntegration } from '@helpers/analytics';
import { useTranslation } from '@hooks';

const PaperStyled = styled(Paper)({
    width: '100%',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px !important',
    marginTop: '16px',
    '@media print': {
        breakInside: 'avoid',
    },
});
const ChartWrapper = styled('div')({
    position: 'relative',
    height: '280px',
    '@media (max-width: 1024px)': {
        height: '400px',
        width: '900px',
    },
    '@media print': {
        height: '280px !important',
    },
});

const createChartData = (labels, data, othersLabel) => {
    return {
        labels,
        datasets: [
            {
                label: othersLabel,
                data: data.map(i => checkIntegration(i).isOthers),
                backgroundColor: 'rgba(188, 201, 206, 1)',
                hoverBackgroundColor: 'rgba(188, 201, 206, 1)',
                categoryPercentage: 1.0,
                barPercentage: 0.7,
                borderWidth: 0,
                borderRadius: 4,
            },
            {
                label: 'Hotels.com',
                data: data.map(i => checkIntegration(i).isHotels),
                backgroundColor: '#CAD5C5',
                hoverBackgroundColor: '#CAD5C5',
                categoryPercentage: 1.0,
                barPercentage: 0.7,
                borderWidth: 0,
                borderRadius: 4,
            },
            {
                label: 'Expedia',
                data: data.map(i => checkIntegration(i).isExpedia),
                backgroundColor: '#D9E2BC',
                hoverBackgroundColor: '#D9E2BC',
                categoryPercentage: 1.0,
                barPercentage: 0.7,
                borderWidth: 0,
                borderRadius: 4,
            },
            {
                label: 'iFood',
                data: data.map(i => checkIntegration(i).isIFood),
                backgroundColor: '#E7EEB3',
                hoverBackgroundColor: '#E7EEB3',
                categoryPercentage: 1.0,
                barPercentage: 0.7,
                borderWidth: 0,
                borderRadius: 4,
            },
            {
                label: 'Trustpilot',
                data: data.map(i => checkIntegration(i).isTrustpilot),
                backgroundColor: '#BDE0CC',
                hoverBackgroundColor: '#BDE0CC',
                categoryPercentage: 1.0,
                barPercentage: 0.7,
                borderWidth: 0,
                borderRadius: 4,
            },
            {
                label: 'TheFork',
                data: data.map(i => checkIntegration(i).isTheFork),
                backgroundColor: '#84B799',
                hoverBackgroundColor: '#84B799',
                categoryPercentage: 1.0,
                barPercentage: 0.7,
                borderWidth: 0,
                borderRadius: 4,
            },
            {
                label: 'Deliveroo',
                data: data.map(i => checkIntegration(i).isDeliveroo),
                backgroundColor: '#416775',
                hoverBackgroundColor: '#416775',
                categoryPercentage: 1.0,
                barPercentage: 0.7,
                borderWidth: 0,
                borderRadius: 4,
            },
            {
                label: 'Booking.com',
                data: data.map(i => checkIntegration(i).isBooking),
                backgroundColor: '#98CBAD',
                hoverBackgroundColor: '#98CBAD',
                categoryPercentage: 1.0,
                barPercentage: 0.7,
                borderWidth: 0,
                borderRadius: 4,
            },
            {
                label: 'Facebook',
                data: data.map(i => checkIntegration(i).isFacebook),
                backgroundColor: 'rgba(98, 130, 141, 1)',
                hoverBackgroundColor: 'rgba(98, 130, 141, 1)',
                categoryPercentage: 1.0,
                barPercentage: 0.7,
                borderWidth: 0,
                borderRadius: 4,
            },
            {
                label: 'Google',
                data: data.map(i => checkIntegration(i).isGoogle),
                backgroundColor: 'rgba(31, 76, 92, 1)',
                hoverBackgroundColor: 'rgba(31, 76, 92, 1)',
                categoryPercentage: 1.0,
                barPercentage: 0.7,
                borderWidth: 0,
                borderRadius: 4,
            },
        ],
    };
};

const chartAreaBorder = {
    id: 'chartAreaBorder',
    beforeDraw(chart) {
        const { ctx } = chart;
        ctx.save();

        ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.lineWidth = chart.config.options.scales.x.grid.lineWidth || 3;

        ctx.beginPath();
        ctx.moveTo(chart.chartArea.right, chart.chartArea.top);
        ctx.lineTo(chart.chartArea.right, chart.chartArea.bottom);
        ctx.stroke();

        ctx.restore();
    },
};

const ReviewsChannelTrend = memo(({ data, language }) => {
    const { t } = useTranslation();

    const formattedData = useMemo(
        () =>
            data.map(item => {
                return item.breakdown.reduce((acc, curr) => {
                    acc[curr.profileType] = curr.totalReviews;
                    return acc;
                }, {});
            }),
        [data],
    );

    const labels = useMemo(() => {
        return data.map(d => format(parseISO(d.date), 'dd MMM', { locale: I18N_DATE_FORMAT[language] }));
    }, [language, data]);

    return (
        <PaperStyled elevation={1}>
            <Typography variant="title1" color="textPrimary" sx={{ marginBottom: '50px' }}>
                {t('AnalyticsReviews.reviewSitesTrend')}
            </Typography>
            <TableContainer>
                <ChartWrapper id="reviews-chart-wrapper">
                    {labels.length && (
                        <Bar
                            parentId="reviews-chart-wrapper"
                            data={createChartData(labels, formattedData, t('AnalyticsReviews.others'))}
                            labels={labels}
                            legends={t('AnalyticsReviews.totalReviews')}
                            optionsProps={{
                                plugins: {
                                    chartAreaBorder,
                                    legend: {
                                        labels: {
                                            filter: function (legendItem, data) {
                                                const index = legendItem.datasetIndex;
                                                const currentDataValue = data.datasets[index];
                                                const isNullable = currentDataValue.data.every(i => i === 0);
                                                return !isNullable;
                                            },
                                        },
                                    },
                                    tooltip: {
                                        filter: tooltipItem => tooltipItem.raw > 0,
                                    },
                                },
                            }}
                        />
                    )}
                </ChartWrapper>
            </TableContainer>
        </PaperStyled>
    );
});

ReviewsChannelTrend.displayName = 'ReviewsChannelTrend';

export default ReviewsChannelTrend;
